import { type User, UserManager, WebStorageStateStore } from 'oidc-client-ts'

// Rauthy is the identity provider; SurrealDB verifies its tokens itself
// (DEFINE ACCESS oidc ... WITH JWT URL). Where Rauthy lives differs per
// environment while the build is the same, so it is read at runtime:
// public/oidc.json for production, overridden by the test nginx.
interface OidcConfig {
  authority: string
  clientId: string
}

let userManager: Promise<UserManager> | undefined

export function getUserManager(): Promise<UserManager> {
  userManager ??= fetch('/oidc.json')
    .then((res) => {
      if (!res.ok) {
        throw new Error(`Failed to load /oidc.json (${res.status})`)
      }
      return res.json() as Promise<OidcConfig>
    })
    .then(({ authority, clientId }) =>
      new UserManager({
        authority,
        client_id: clientId,
        redirect_uri: window.location.origin + '/callback',
        post_logout_redirect_uri: window.location.origin + '/',
        scope: 'openid profile',
        userStore: new WebStorageStateStore({ store: window.localStorage }),
        // Renews with the refresh token shortly before the access token expires
        automaticSilentRenew: true,
      }),
    )
    .catch((err) => {
      userManager = undefined // allow a retry
      throw err
    })
  return userManager
}

/** The signed-in user, or a redirect to Rauthy that never resolves. */
export async function requireUser(): Promise<User> {
  const manager = await getUserManager()
  const user = await manager.getUser()
  if (user && !user.expired) {
    return user
  }

  if (user?.refresh_token) {
    try {
      const renewed = await manager.signinSilent()
      if (renewed) {
        return renewed
      }
    }
    catch (err) {
      console.warn('Token renewal failed, signing in again:', err)
    }
  }

  await signIn(window.location.pathname + window.location.search)
  return new Promise(() => {}) // the browser is navigating away
}

export async function signIn(returnUrl = '/') {
  const manager = await getUserManager()
  await manager.signinRedirect({ state: returnUrl })
}

/** Ends the session here and at Rauthy, which then sends the browser back to "/". */
export async function signOut() {
  const manager = await getUserManager()
  await manager.signoutRedirect()
}

/** Who is signed in, from the ID token's profile claims; Rauthy's preferred_username is the email. */
export async function signedInAs(): Promise<{ name: string, email?: string }> {
  const profile = (await (await getUserManager()).getUser())?.profile
  const email = profile?.email ?? (profile?.preferred_username?.includes('@') ? profile.preferred_username : undefined)
  const fullName = [profile?.given_name, profile?.family_name].filter(Boolean).join(' ')
  return { name: profile?.name || fullName || email || '', email }
}

/** Rauthy's page where users manage their own account (profile, password, MFA). */
export async function accountUrl(): Promise<string> {
  const manager = await getUserManager()
  return manager.settings.authority + '/account'
}
